import './settings.css';
import { renderProfile, attachProfile } from './profile';
import { supabase } from '../lib/supabase';
import { isAdmin } from '../services/access';
import { fetchSharedOwners } from '../services/sharing';
import { toast } from '../lib/ui';

/** כפיית עדכון גרסה: מנקה קאשים, מבטל את ה-service worker ומרענן — כדי לקבל את
 *  הגרסה החדשה מיד, בלי צורך לסגור ולפתוח את האפליקציה. */
async function forceUpdate(): Promise<void> {
  toast('מעדכן לגרסה החדשה…');
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch { /* גם אם הניקוי נכשל — reload יביא לפחות ניסיון רענון */ }
  // reload עם עקיפת קאש (query fresh) כדי להבטיח משיכה מחדש
  const url = new URL(location.href);
  url.searchParams.set('_v', Date.now().toString());
  location.replace(url.toString());
}

export interface SettingsNav {
  openViewer: () => void;
  openAdmin: () => void;
}

// ---------- תפריט ההגדרות ----------
// המסך = תפריט מקובץ בסגנון iOS (אייקון + כותרת + כותרת-משנה + חץ). "פרופיל ויעדים"
// נפתח כמסך-משנה בתוך טאב ההגדרות (החלפת innerHTML במקום), עם חץ חזרה לתפריט.

/** שורת תפריט אחת. arrow=false לשורות פעולה (עדכון/התנתקות) שאינן ניווט פנימה. */
function entryHtml(opts: {
  id: string;
  icon: string;
  title: string;
  subtitle?: string;
  arrow?: boolean;
  danger?: boolean;
  extra?: boolean;
}): string {
  const { id, icon, title, subtitle, arrow = true, danger = false, extra = false } = opts;
  const cls = `settings-entry${danger ? ' settings-entry--danger' : ''}`;
  return `
    <button type="button" class="${cls}" id="${id}"${extra ? ' data-extra' : ''}>
      <span class="se-ic">${icon}</span>
      <span class="se-txt"><b>${title}</b>${subtitle ? `<small>${subtitle}</small>` : ''}</span>
      ${arrow ? '<span class="se-arrow"><svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 6 9 12 15 18"/></svg></span>' : ''}
    </button>`;
}

/** תפריט ההגדרות הראשי. הכניסות המותנות (ליווי/ניהול) מתווספות ברקע לכרטיס הראשי. */
function menuHtml(): string {
  return `
    <div class="settings-menu">
      <div class="card settings-entries-card" id="settings-main-card">
        ${entryHtml({
          id: 'entry-profile',
          icon: '🎯',
          title: 'פרופיל ויעדים',
          subtitle: 'נתונים אישיים, יעד קלוריות ומקרו',
        })}
      </div>

      <div class="card settings-entries-card">
        ${entryHtml({
          id: 'entry-version',
          icon: '🔄',
          title: `גרסה ${__BUILD_ID__}`,
          subtitle: 'הקישי לעדכון',
          arrow: false,
        })}
        ${entryHtml({
          id: 'entry-logout',
          icon: '🚪',
          title: 'התנתקות',
          arrow: false,
          danger: true,
        })}
      </div>
    </div>`;
}

export function renderSettings(): string {
  return menuHtml();
}

export function attachSettings(root: HTMLElement, nav: SettingsNav): void {
  attachMenu(root, nav);
}

/** חיבור מאזיני התפריט הראשי (על ה-DOM הקיים). נקרא בעליית המסך ובחזרה מהפרופיל. */
function attachMenu(root: HTMLElement, nav: SettingsNav): void {
  root.querySelector('#entry-profile')?.addEventListener('click', () => openProfile(root, nav));
  root.querySelector('#entry-version')?.addEventListener('click', () => void forceUpdate());
  root.querySelector('#entry-logout')?.addEventListener('click', async () => {
    await supabase.auth.signOut();
  });
  // כניסות מותנות — נטענות ברקע ומתווספות לכרטיס הראשי (מעל קבוצת המערכת)
  void loadConditionalEntries(root, nav);
}

/** פתיחת מסך-משנה של הפרופיל בתוך טאב ההגדרות (החלפה במקום). */
function openProfile(root: HTMLElement, nav: SettingsNav): void {
  root.innerHTML = `<button type="button" class="subscreen-back" id="settings-back"><svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 6 15 12 9 18"/></svg> הגדרות</button>${renderProfile()}`;
  root.querySelector('#settings-back')?.addEventListener('click', () => backToMenu(root, nav));
  attachProfile(root);
  window.scrollTo(0, 0);
}

/** חזרה מהפרופיל לתפריט — רינדור מחדש של התפריט וחיבור מאזיניו. */
function backToMenu(root: HTMLElement, nav: SettingsNav): void {
  root.innerHTML = menuHtml();
  attachMenu(root, nav);
  window.scrollTo(0, 0);
}

async function loadConditionalEntries(root: HTMLElement, nav: SettingsNav): Promise<void> {
  const [admin, owners] = await Promise.all([
    isAdmin().catch(() => false),
    fetchSharedOwners().catch(() => [] as string[]),
  ]);
  if (!root.isConnected) return;
  // רק כשהתפריט מוצג (לא במסך-משנה של הפרופיל)
  const card = root.querySelector('#settings-main-card');
  if (!card) return;

  const entries: string[] = [];
  if (owners.length > 0) {
    entries.push(entryHtml({
      id: 'entry-viewer',
      icon: '👀',
      title: 'תצוגת הליווי',
      subtitle: 'היומן ששותף איתך, קריאה בלבד',
      extra: true,
    }));
  }
  if (admin) {
    entries.push(entryHtml({
      id: 'entry-admin',
      icon: '🔑',
      title: 'ניהול משתמשים',
      subtitle: 'אישור נרשמים חדשים',
      extra: true,
    }));
  }

  // ניקוי כניסות-רקע קודמות (מגן מפני הזרקה כפולה במרוצי async) ואז הוספה
  card.querySelectorAll('[data-extra]').forEach((el) => el.remove());
  if (entries.length === 0) return;
  card.insertAdjacentHTML('beforeend', entries.join(''));
  card.querySelector('#entry-viewer')?.addEventListener('click', nav.openViewer);
  card.querySelector('#entry-admin')?.addEventListener('click', nav.openAdmin);
}
